import { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/hooks/useAuth";
import { useMyAffiliates } from "@/hooks/useMyAffiliates";
import ProfileTab from "@/components/affiliate-dashboard/ProfileTab";
import LeadsTab from "@/components/affiliate-dashboard/LeadsTab";

const AffiliateDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const { affiliates, loading, refetch } = useMyAffiliates();
  const [activeId, setActiveId] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!activeId && affiliates[0]) setActiveId(affiliates[0].id);
  }, [affiliates, activeId]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to={`/auth?next=/affiliate/dashboard`} replace />;
  }

  if (affiliates.length === 0) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <SiteHeader />
        <section className="pt-36 pb-24">
          <div className="container mx-auto px-6 max-w-2xl text-center">
            <h1 className="font-display text-3xl font-bold mb-3">No affiliate listing yet</h1>
            <p className="text-muted-foreground mb-6">
              We couldn't find an affiliate profile linked to {user.email}. If you've
              applied recently, our team is reviewing your application. Otherwise, you
              can apply now to join the directory.
            </p>
            <button
              onClick={() => navigate("/affiliates/apply")}
              className="text-primary hover:underline"
            >
              Apply to become an affiliate →
            </button>
          </div>
        </section>
        <SiteFooter />
      </div>
    );
  }

  const active = affiliates.find((a) => a.id === activeId) ?? affiliates[0];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <section className="pt-36 pb-24">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
            <div>
              <span className="text-primary text-sm font-semibold tracking-wider uppercase">
                Affiliate dashboard
              </span>
              <h1 className="font-display text-3xl md:text-4xl font-bold mt-2">
                Manage your listing &amp; leads
              </h1>
            </div>
            {affiliates.length > 1 ? (
              <Select value={active.id} onValueChange={setActiveId}>
                <SelectTrigger className="w-64"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {affiliates.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
          </div>

          <Tabs defaultValue="leads" className="w-full">
            <TabsList>
              <TabsTrigger value="leads">Leads</TabsTrigger>
              <TabsTrigger value="profile">Profile</TabsTrigger>
            </TabsList>
            <TabsContent value="leads" className="mt-6">
              <LeadsTab affiliateIds={[active.id]} />
            </TabsContent>
            <TabsContent value="profile" className="mt-6">
              <ProfileTab affiliate={active} onSaved={() => refetch()} />
            </TabsContent>
          </Tabs>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
};

export default AffiliateDashboard;